import { useEffect, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  pollDistribution,
  type BlogPoll as BlogPollData,
  type BlogPollCount,
} from '@jeraisonne/db';

interface BlogPollProps {
  poll: BlogPollData;
  /** Index choisi par le lecteur, ou null s'il n'a pas encore choisi. */
  answeredChoice: number | null;
  /** Distribution agrégée (incluant éventuellement le vote du lecteur). */
  counts: BlogPollCount[];
  onVote: (choiceIndex: number) => void;
  pending: boolean;
  /** Dévoile les barres de distribution. Défaut `true` (un vote enregistré montre toujours les résultats). */
  revealCounts?: boolean;
  /** Lecture seule (version « article complet » `?full=1`). */
  readOnly?: boolean;
}

/** Durée du remplissage animé des jauges (ms) — même cadence que success-rate-ring. */
const FILL_MS = 1000;

/** Porté depuis apps/marketing/src/components/blog-poll.tsx (sans la partie newsletter). */
export function BlogPoll({
  poll,
  answeredChoice,
  counts,
  onVote,
  pending,
  revealCounts = true,
  readOnly = false,
}: BlogPollProps) {
  const chosen = answeredChoice !== null;
  const showResults = (chosen || readOnly) && revealCounts;
  const locked = chosen || pending || readOnly;
  const dist = pollDistribution(counts, poll.choices.length);

  const [anim, setAnim] = useState(0);
  useEffect(() => {
    if (!showResults) return;
    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduced ? 0 : FILL_MS;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = duration <= 0 ? 1 : Math.min((now - start) / duration, 1);
      setAnim(1 - Math.pow(1 - p, 3));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [showResults]);

  return (
    <div className="border-primary-400 from-primary-500 to-primary-600 bg-linear-to-b mt-4 rounded-2xl border p-4 shadow-md">
      <p className="text-level-100 mb-2 text-[11px] font-bold uppercase tracking-wide">
        {readOnly && !chosen ? 'Le sondage' : 'Ton avis'}
      </p>
      <p className="mb-3 text-sm font-semibold text-white">{poll.question}</p>

      <div className="flex flex-col gap-2">
        {poll.choices.map((choice, i) => {
          const isChosen = answeredChoice === i;
          const pct = dist.percentages[i] ?? 0;
          const shownPct = Math.round(anim * pct);
          return (
            <button
              key={i}
              type="button"
              disabled={locked}
              onClick={() => onVote(i)}
              aria-pressed={isChosen}
              className={cn(
                'w-full rounded-xl border bg-white/10 px-3 py-2.5 text-left text-sm font-medium text-white transition active:scale-[0.99]',
                isChosen ? 'border-level-400 ring-level-400/50 ring-1' : 'border-white/25',
                !locked && 'hover:border-white/60 hover:bg-white/15',
                chosen && !isChosen && 'opacity-80',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-white">{choice}</span>
                {showResults && (
                  <span
                    className={cn(
                      'shrink-0 text-xs font-semibold tabular-nums',
                      isChosen ? 'text-white' : 'text-white/70',
                    )}
                  >
                    {shownPct}%
                  </span>
                )}
              </span>
              {showResults && (
                <span
                  aria-hidden
                  className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-white/15"
                >
                  <span
                    className={cn(
                      'block h-full rounded-full transition-none',
                      isChosen ? 'bg-level-400 shadow-glow-level' : 'bg-white/45',
                    )}
                    style={{ width: `${(anim * pct).toFixed(2)}%` }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {showResults && (
        <p className="mt-2 text-[11px] text-white/70">
          {dist.total} vote{dist.total > 1 ? 's' : ''}
          {chosen ? ' — voici comment ton avis se compare.' : '.'}
        </p>
      )}
    </div>
  );
}
